import { marked } from 'marked'

import { type StreamResponseStep } from '~hooks/useStreamAIResponse'
import { getStartedLabel } from '~pages/AISettingsScreen/components/ChatStreamSteps'
import {
  type AIInteractionTranscript,
  AgentActionTypeEnum,
  type IAIInteractionMessageDisplay,
  type SourceDisplayLink,
} from '~types'

/**
 * Converts an AIInteractionTranscript to an array of messages for display.
 *
 * @param aiInteractionTranscript - The AI interaction transcript to convert
 * @param options - Optional configuration
 * @param options.useEnglish - Whether to use English translations when available
 * @returns Array of messages sorted by timestamp
 */
export const convertToLogMessages = (
  aiInteractionTranscript: AIInteractionTranscript,
  options: {
    useEnglish?: boolean
  } = {},
): IAIInteractionMessageDisplay[] => {
  const { useEnglish = false } = options
  const messages: IAIInteractionMessageDisplay[] = []

  // Add query messages
  aiInteractionTranscript.queries.forEach((query) => {
    const rawAttachments = (query as { attachments?: unknown[] }).attachments ?? []
    messages.push({
      id: query.id || null,
      text: useEnglish && query.english_text ? query.english_text : query.body,
      sender: 'USER',
      timestamp: new Date(query.created || new Date()),
      impression: null,
      sources: [],
      attachments: rawAttachments.map((a) => {
        const att = a as {
          filename?: string
          download_url?: string
          is_image?: boolean
          preview_url?: string | null
        }
        return {
          filename: att.filename ?? '',
          download_url: att.download_url ?? '',
          is_image: att.is_image ?? false,
          preview_url: att.preview_url ?? null,
        }
      }),
    })
  })

  // Add response messages
  aiInteractionTranscript.responses.forEach((response) => {
    const text = marked.parse(
      useEnglish && response.english_text ? response.english_text : response.body,
      { breaks: true },
    )
    messages.push({
      id: response.id || null,
      text: text as string,
      sender: 'AI',
      responseType: response.type,
      sources: response.sources ?? [],
      cases: response.cases ?? undefined,
      contacts: response.contacts ?? undefined,
      files: response.files ?? undefined,
      skills: response.skills ?? undefined,
      hasIntegrations: response.has_integrations ?? undefined,
      timestamp: new Date(response.created || new Date()),
      impression: response.impression ?? null,
      feedback: response.feedback ?? undefined,
      submitter: response.submitter ?? undefined,
      feedbackCreated: response.feedback_created ? new Date(response.feedback_created) : undefined,
    })
  })

  // Add agent action messages
  aiInteractionTranscript.agent_actions.forEach((action) => {
    messages.push({
      id: action.id || null,
      text: action.body,
      sender: 'ACTION',
      actionType: action.action_type,
      sources:
        action.action_type === AgentActionTypeEnum.Enum.SEARCH_RESULTS
          ? ((action.metadata as { results?: SourceDisplayLink[] } | null)?.results ?? [])
          : [],
      timestamp: new Date(action.created || new Date()),
      impression: null,
    })
  })

  return messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

/**
 * Gets a user-friendly text description for the current streaming step.
 *
 * @param step - The current streaming response step
 * @returns A human-readable description of what the AI is doing
 */
export const getStreamingIndicatorText = (step?: StreamResponseStep): string =>
  getStartedLabel(step ?? '')

/**
 * Creates a user message object.
 *
 * @param text - The message text
 * @param options - Optional message properties
 * @returns A formatted user message
 */
export const createUserMessage = (
  text: string,
  options: Partial<IAIInteractionMessageDisplay> = {},
): IAIInteractionMessageDisplay => ({
  id: null,
  text,
  sender: 'USER',
  timestamp: new Date(),
  impression: null,
  sources: [],
  ...options,
})

/**
 * Creates an AI message object.
 *
 * @param text - The message text
 * @param options - Optional message properties
 * @returns A formatted AI message
 */
export const createAIMessage = (
  text: string,
  options: Partial<IAIInteractionMessageDisplay> = {},
): IAIInteractionMessageDisplay => ({
  id: null,
  text,
  sender: 'AI',
  timestamp: new Date(),
  impression: null,
  sources: [],
  ...options,
})

/**
 * Creates an error message object.
 *
 * @param errorText - The error message text
 * @param options - Optional message properties
 * @returns A formatted error message
 */
export const createErrorMessage = (
  errorText: string,
  options: Partial<IAIInteractionMessageDisplay> = {},
): IAIInteractionMessageDisplay => ({
  id: null,
  text: errorText,
  sender: 'AI',
  timestamp: new Date(),
  impression: null,
  sources: [],
  ...options,
})
